import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import { toast } from "react-toastify";
import ComponentCard from "../../components/common/ComponentCard";
import PageMeta from "../../components/common/PageMeta";
import useGoBack from "../../hooks/useGoBack";
import SubscriptionPlanService from "../../services/subscription-plan.service";
import SubscriptionPlanForm from "./SubscriptionPlanForm";
import { SubscriptionPlan, SubscriptionPlanFormData, SubscriptionPlanFormErrors } from "./types";
import {
  buildPlanPayload,
  defaultPlanFormData,
  validatePlanForm,
} from "./subscriptionPlanFormUtils";

const mapPlanToForm = (plan: SubscriptionPlan): SubscriptionPlanFormData => ({
  name: plan.name || "",
  description: plan.description || "",
  price: plan.price?.toString() || "",
  currency: plan.currency || "usd",
  interval: plan.interval === "year" ? "year" : "month",
  appleProductId: plan.appleProductId || "",
  googleProductId: plan.googleProductId || "",
  featuresText: (plan.features || []).join("\n"),
  isPopular: Boolean(plan.isPopular),
  isActive: plan.isActive !== false,
});

export default function EditSubscriptionPlan() {
  const { plan_id } = useParams();
  const [formData, setFormData] =
    useState<SubscriptionPlanFormData>(defaultPlanFormData);
  const [errors, setErrors] = useState<SubscriptionPlanFormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const goBack = useGoBack();

  useEffect(() => {
    const fetchPlan = async () => {
      if (!plan_id) return;
      try {
        setFetching(true);
        const response = await SubscriptionPlanService.getPlan(plan_id);
        const plan = response.data?.data || response.data;
        setFormData(mapPlanToForm(plan));
      } catch (err) {
        console.error("Error fetching subscription plan:", err);
        toast.error("Failed to fetch subscription plan.");
      } finally {
        setFetching(false);
      }
    };

    fetchPlan();
  }, [plan_id]);

  const handleChange = (
    field: keyof SubscriptionPlanFormData,
    value: string | boolean
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleEdit = async () => {
    if (!plan_id) return;
    const nextErrors = validatePlanForm(formData);
    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;

    setIsLoading(true);
    try {
      const response = await SubscriptionPlanService.updatePlan(
        plan_id,
        buildPlanPayload(formData)
      );
      if (response.data?.status === 200 || response.status === 200) {
        toast.success("Subscription plan updated successfully!");
        setTimeout(() => {
          window.location.href = "/list-subscription-plans";
        }, 1000);
      }
    } catch (err) {
      console.error("Error updating subscription plan:", err);
      toast.error("Failed to update subscription plan. Please try again.");
      setIsLoading(false);
    }
  };

  return (
    <div>
      <PageMeta
        title="VIYUME - Edit Subscription Plan"
        description="Edit Subscription Plan"
      />
      <div className="mx-auto max-w-[80%] grid grid-cols-1 gap-6">
        <ComponentCard title="">
          {fetching ? (
            <div className="space-y-4">
              {[...Array(6)].map((_, index) => (
                <div
                  key={index}
                  className="h-11 animate-pulse rounded bg-gray-200 dark:bg-gray-700"
                />
              ))}
            </div>
          ) : (
            <SubscriptionPlanForm
              data={formData}
              errors={errors}
              isLoading={isLoading}
              submitLabel="Edit"
              loadingLabel="Editing..."
              onChange={handleChange}
              onCancel={goBack}
              onSubmit={handleEdit}
            />
          )}
        </ComponentCard>
      </div>
    </div>
  );
}
